import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '@/lib/supabase/client';
import { AuthState, User } from '../types/auth'; 
import type { AuthError } from '@supabase/supabase-js'; 

export const useAuthStore = create<AuthState>()( 
  persist(
    (set, get) => ({
      user: null,
      isLoading: true, // Start loading by default
      error: null,
      initialized: false, // Start as false, set to true after first refreshUser attempt

      setUser: (user: User | null) => set({ user, isLoading: false }),
      setInitialized: (status: boolean) => set({ initialized: status }),

      refreshUser: async () => {
        
        const currentUser = get().user;
        const currentIsLoading = get().isLoading;

        if (!currentUser && !currentIsLoading) { // Only set isLoading if no user and not already loading
          
          set({ isLoading: true, error: null });
        } else if (currentUser) {
          
        } else { // No current user but already loading
          
        }

        try {
          
          
          const { data: { session }, error: sessionError } = await supabase.auth.getSession();
          

          if (sessionError) {
            
            throw sessionError;
          }

          if (session?.user) {
            
            
            const { data: userData, error: profileError } = await supabase
              .from('users') 
              .select('*')
              .eq('id', session.user.id)
              .single();
            
            
            if (profileError) {
              
              set({ user: null, error: `Failed to fetch user profile: ${profileError.message}` });
              return; 
            }
            
            set({ user: userData || null, error: null });
          } else {
            
            set({ user: null }); 
          }
        } catch (error) {
          const e = error as AuthError | Error;
          
          if (!get().error?.includes('Failed to fetch user profile')) {
            set({ 
              user: null,
              error: e.message || 'Failed to refresh user data',
            });
          }
        } finally {
          
          set({ isLoading: false, initialized: true });
        }
      },

      logout: async () => {
        try {
          set({ isLoading: true, error: null });
          await supabase.auth.signOut();
          set({ user: null, isLoading: false, error: null }); 
        } catch (error) {
          const e = error as AuthError | Error;
          
          set({ 
            isLoading: false, 
            error: e.message || 'Failed to sign out',
          });
        }
      },
    }),
    {
      name: 'auth-storage', 
      onRehydrateStorage: () => (state) => {
        
        if (state) {
          state.user = null;        // Reset user
          state.error = null;       // Reset error
          state.initialized = false;
          state.isLoading = true; 
        }
      },
    }
  )
);

// Optional: Trigger initial auth check when store is defined
// if (typeof window !== 'undefined') { 
//   useAuthStore.getState().refreshUser();
// }